import Microgame from '../engine/Microgame.js';

export default class PhotoGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Clic Clac !`, en: `Photo!`, es: `¡Foto!` },
            controls: { fr: `Appuie au bon moment pour centrer la photo`, en: `Tap at the right time to center the photo`, es: `Toca en el momento adecuado para centrar la foto` },
            objective: { fr: `Prends la photo parfaite quand le sujet est centré`, en: `Take the perfect picture when the subject is centered`, es: `Toma la foto perfecta cuando el sujeto esté centrado` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>`,
            actions: [
                { type: 'wait', duration: 1000 },
                { type: 'tap', x: 50, y: 50, duration: 220 },
                { type: 'wait', duration: 820 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.subject = null;
        this.frame = null;
        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.3;
        this.duration = 2000 * (speedMult / adj);

        // Randomize starting side
        const startLeft = this.isPlayback ? true : (Math.random() < 0.5);
        this.subjectX = startLeft ? -10 : 110;

        // Start slower (1.6 instead of 3), and scale up with difficulty
        this.baseSpeed = 1.6 + (speedMult - 1) * 1.0;
        this.subjectSpeed = startLeft ? this.baseSpeed : -this.baseSpeed;
        this.timeMs = 0;
        this.triggered = false;
        this._isAnimating = false;
        this._frozenTimerValue = 0;

        // Ghost pause logic
        this.shouldPause = this.isPlayback ? false : (Math.random() < 0.25);
        this.hasPaused = false;
        this.isPaused = false;
        this.pauseTime = 50; // ms
        // Random side to pause on (left < 32 or right > 68)
        this.pauseAtX = this.isPlayback ? 0 : (Math.random() < 0.5 ? Math.random() * 20 : 80 + Math.random() * 20);
    }

    setup() {
        this.engine.ui.instruction.textContent = "SNAP!";

        // Viewfinder Frame
        this.frame = document.createElement('div');
        this.frame.style.position = 'absolute';
        this.frame.style.width = '150px';
        this.frame.style.height = '150px';
        this.frame.style.border = `4px dashed ${this.primaryColor}`;
        this.frame.style.top = '50%';
        this.frame.style.left = '50%';
        this.frame.style.transform = 'translate(-50%, -50%)';
        this.frame.style.boxShadow = `0 0 20px ${this.primaryColor}, 0 0 0 9999px rgba(0,0,0,0.8)`; // Neon + Darken outside
        this.frame.style.zIndex = '10';
        this.container.appendChild(this.frame);

        // Subject (e.g. a ghost or alien)
        this.subject = document.createElement('div');
        this.subject.style.position = 'absolute';
        this.subject.style.width = '80px';
        this.subject.style.height = '100px';
        this.subject.style.background = this.secondaryColor;
        this.subject.style.borderRadius = '40px 40px 0 0';
        this.subject.style.top = '50%';
        this.subject.style.left = `${this.subjectX}%`; 
        this.subject.style.transform = 'translate(-50%, -50%)';
        this.subject.style.boxShadow = `0 0 20px ${this.secondaryColor}`;
        this.container.appendChild(this.subject);

        // Eyes
        const eye = document.createElement('div');
        eye.style.position = 'absolute';
        eye.style.width = '10px';
        eye.style.height = '10px';
        eye.style.background = '#000';
        eye.style.borderRadius = '50%';
        eye.style.top = '30px';
        eye.style.left = '20px';
        this.subject.appendChild(eye);

        const eye2 = eye.cloneNode();
        eye2.style.left = '50px';
        this.subject.appendChild(eye2);

        this.onTap = this.onTap.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onTap);

        this.animate();
    }

    animate() {
        if (!this.active) return;

        if (this._isAnimating) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
        } else {
            // Pause logic
            if (this.shouldPause && !this.hasPaused && !this.isPaused) {
                // Check if we hit the pause trigger X
                if (Math.abs(this.subjectX - this.pauseAtX) < Math.abs(this.subjectSpeed)) {
                    this.isPaused = true;
                    setTimeout(() => {
                        this.isPaused = false;
                        this.hasPaused = true;
                    }, this.pauseTime);
                }
            }

            if (!this.isPaused) {
                this.subjectX += this.subjectSpeed;
                this.timeMs += 16.67;

                // Bounce
                if (this.subjectX > 115 || this.subjectX < -15) {
                    this.subjectSpeed *= -1;
                }
            }
        }

        // Float effect on Y
        const offsetY = Math.sin(this.timeMs / 400) * 15;

        this.subject.style.left = `${this.subjectX}%`;
        this.subject.style.transform = `translate(-50%, calc(-50% + ${offsetY}px))`;

        requestAnimationFrame(() => this.animate());
    }

    onTap() {
        if (this.triggered || this.won || this.lost) return;
        this.triggered = true;

        // Check if subject is in frame (center is 50%)
        // Frame is 150px wide. Container width approx 400px.
        // 150px is approx 37% of width.
        // So center +/- 18%.
        // 32% to 68%.

        const isHit = this.subjectX > 32 && this.subjectX < 68;
        
        this._isAnimating = true; 
        this._frozenTimerValue = this.engine.timer;
        this.subjectSpeed = 0;
        
        if (isHit) {
            this.subject.style.background = this.primaryColor; // Captured visual
        }

        // Generate polaroid photo validating the capture (or miss)
        const photo = document.createElement('div');
        photo.style.position = 'absolute';
        photo.style.top = '50%';
        photo.style.left = '50%';
        photo.style.width = '170px';
        photo.style.height = '210px';
        photo.style.background = '#fff';
        photo.style.zIndex = '35';
        photo.style.transform = 'translate(-50%, -45%) rotate(-4deg) scale(1.1)';
        photo.style.boxShadow = '0 10px 40px rgba(0,0,0,0.8)';
        photo.style.opacity = '0';
        photo.style.transition = 'opacity 0.1s 0.05s, transform 0.3s 0.05s cubic-bezier(0.175, 0.885, 0.32, 1.275)';

        const photoInner = document.createElement('div');
        photoInner.style.position = 'absolute';
        photoInner.style.top = '10px';
        photoInner.style.left = '10px';
        photoInner.style.width = '150px';
        photoInner.style.height = '150px';
        photoInner.style.background = '#0a0a0a';
        photoInner.style.overflow = 'hidden';
        photo.appendChild(photoInner);

        // Clone ghost inside photo (will be off-screen if miss)
        const clone = this.subject.cloneNode(true);
        const cw = this.container.clientWidth || 400;
        const ghostAbsX = (this.subjectX / 100) * cw;
        const frameCenter = cw / 2;
        const offsetX = ghostAbsX - frameCenter;
        
        clone.style.left = `calc(50% + ${offsetX}px)`;
        clone.style.top = '50%';
        clone.style.boxShadow = 'none';
        clone.style.zIndex = '1';
        photoInner.appendChild(clone);

        this.container.appendChild(photo);
        
        // Pop the polaroid onto the screen
        setTimeout(() => {
            photo.style.opacity = '1';
            photo.style.transform = 'translate(-50%, -50%) rotate(3deg) scale(1.3)';
            this.frame.style.display = 'none';
            this.subject.style.display = 'none';
            
            // End after a short delay
            setTimeout(() => {
                if (isHit) this.win();
                else this.lose();
            }, 550);
        }, 50);
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onTap);
    }
}
